import React, { useState } from 'react';
import { createGame } from '../gameEngine';

function NumberPicker({ value, onChange, min = 1, max = 20 }) {
  return (
    <div className="number-row">
      <button className="number-btn" onClick={() => onChange(Math.max(min, value - 1))}>−</button>
      <span className="number-val">{value === 99 ? '∞' : value}</span>
      <button className="number-btn" onClick={() => onChange(Math.min(max, value + 1))}>+</button>
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="toggle-slider" />
    </label>
  );
}

export default function NewGame({ navigate }) {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    homeTeam: '',
    awayTeam: '',
    umpireName: '',
    maxInnings: 7,
    maxStrikes: 3,
    maxBalls: 4,
    maxOuts: 3,
    mercyEnabled: false,
    mercyRuns: 7,
    gameMercyEnabled: false,
    gameMercyRuns: 15,
    timeLimitEnabled: false,
    timeLimitMinutes: 60,
    extraInnings: true,
  });

  const set = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  const handleStart = async () => {
    if (!settings.homeTeam.trim() || !settings.awayTeam.trim()) {
      alert('Please enter both team names.');
      return;
    }
    setLoading(true);
    try {
      const game = await createGame(settings);
      navigate('umpire', game);
    } catch (e) {
      alert('Error creating game: ' + e.message);
      setLoading(false);
    }
  };

  return (
    <div className="screen">
      <div className="header">
        <button className="header-back" onClick={() => navigate('home')}>‹</button>
        <span className="header-title">New Game</span>
      </div>

      <div className="scroll-content">
        <div className="form-section">
          <div className="form-group">
            <label className="form-label">Away Team</label>
            <input className="form-input" placeholder="Away Team Name" value={settings.awayTeam}
              onChange={e => set('awayTeam', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Home Team</label>
            <input className="form-input" placeholder="Home Team Name" value={settings.homeTeam}
              onChange={e => set('homeTeam', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Umpire Name (optional)</label>
            <input className="form-input" placeholder="Umpire Name" value={settings.umpireName}
              onChange={e => set('umpireName', e.target.value)} />
          </div>
        </div>

        <div className="section-title">Game Rules</div>

        <div className="form-section">
          {/* Innings */}
          <div className="toggle-row">
            <div>
              <div className="toggle-label">Innings</div>
              <div className="toggle-sub">How many innings</div>
            </div>
            <NumberPicker value={settings.maxInnings} min={1} max={15}
              onChange={v => set('maxInnings', v)} />
          </div>

          {/* Strikes */}
          <div className="toggle-row">
            <div>
              <div className="toggle-label">Strikes per At-Bat</div>
            </div>
            <NumberPicker value={settings.maxStrikes} min={2} max={4}
              onChange={v => set('maxStrikes', v)} />
          </div>

          {/* Balls */}
          <div className="toggle-row">
            <div>
              <div className="toggle-label">Balls per At-Bat</div>
            </div>
            <NumberPicker value={settings.maxBalls} min={3} max={5}
              onChange={v => set('maxBalls', v)} />
          </div>

          {/* Outs */}
          <div className="toggle-row">
            <div>
              <div className="toggle-label">Outs per Inning</div>
            </div>
            <NumberPicker value={settings.maxOuts} min={1} max={4}
              onChange={v => set('maxOuts', v)} />
          </div>

          {/* Extra Innings */}
          <div className="toggle-row">
            <div>
              <div className="toggle-label">Extra Innings</div>
              <div className="toggle-sub">Allow play past max innings if tied</div>
            </div>
            <Toggle checked={settings.extraInnings} onChange={v => set('extraInnings', v)} />
          </div>

          {/* Time Limit */}
          <div className="toggle-row">
            <div>
              <div className="toggle-label">Time Limit</div>
              <div className="toggle-sub">Game ends after a set time</div>
            </div>
            <Toggle checked={settings.timeLimitEnabled} onChange={v => set('timeLimitEnabled', v)} />
          </div>

          {settings.timeLimitEnabled && (
            <div className="toggle-row">
              <div>
                <div className="toggle-label">Minutes</div>
              </div>
              <NumberPicker value={settings.timeLimitMinutes} min={15} max={180}
                onChange={v => set('timeLimitMinutes', v)} />
            </div>
          )}

          {/* Inning Mercy */}
          <div className="toggle-row">
            <div>
              <div className="toggle-label">Inning Mercy Rule</div>
              <div className="toggle-sub">End inning after X runs scored</div>
            </div>
            <Toggle checked={settings.mercyEnabled} onChange={v => set('mercyEnabled', v)} />
          </div>

          {settings.mercyEnabled && (
            <div className="toggle-row">
              <div>
                <div className="toggle-label">Runs to end inning</div>
              </div>
              <NumberPicker value={settings.mercyRuns} min={3} max={20}
                onChange={v => set('mercyRuns', v)} />
            </div>
          )}

          {/* Game Mercy */}
          <div className="toggle-row">
            <div>
              <div className="toggle-label">Game Mercy Rule</div>
              <div className="toggle-sub">End game if run differential reached</div>
            </div>
            <Toggle checked={settings.gameMercyEnabled} onChange={v => set('gameMercyEnabled', v)} />
          </div>

          {settings.gameMercyEnabled && (
            <div className="toggle-row">
              <div>
                <div className="toggle-label">Run differential</div>
              </div>
              <NumberPicker value={settings.gameMercyRuns} min={5} max={30}
                onChange={v => set('gameMercyRuns', v)} />
            </div>
          )}
        </div>

        <div style={{ padding: '8px 24px 24px' }}>
          <button className="btn btn-primary btn-lg" onClick={handleStart} disabled={loading}>
            {loading ? 'Creating Game...' : '⚾ Play Ball!'}
          </button>
        </div>
      </div>
    </div>
  );
}
